/**
 * A Typst exercise body as a list of editable parts, and back.
 *
 * The exercise editor's form view and the sheet editor's Item tab both show a Typst
 * exercise as fields — statement, subtasks with points, solution, hints, choices — while
 * the source stays the one thing that is stored and co-edited. So this module never
 * owns the text: it reads a source into nodes that know their exact ranges, and every
 * edit comes back as one minimal `{from, to, insert}` change against that source, which
 * the caller applies to its string or Y.Text. Two people typing in the same body, one in
 * the form and one in the source, then edit the same characters and nothing else.
 *
 * Three rules make that safe:
 *
 *  - Lossless. `serializeExercise(parseExercise(s)) === s` for *any* string, garbage
 *    included. Anything the parser does not understand — an unclosed bracket, a `#let`,
 *    a `#show` rule, a call it has no form for — becomes a `raw` node holding its text
 *    verbatim, which the form shows as "Raw source" and never rewrites.
 *  - Block level only. A structured node is recognised where it starts a line and ends
 *    one. `#hint[…]` in the middle of a sentence stays part of the statement: splitting
 *    a paragraph into three form fields would be worse than leaving it as text.
 *  - Canonical writing. New parts are written in the course vocabulary — a solution is
 *    `#solution[…]` — while the older `#answer[…]` is still read.
 *
 * Pure: no imports beyond this package, no DOM, no Node.
 */

/* ------------------------------------------------------------------- types */

export type BlockKind =
  | 'hint'
  | 'note'
  | 'example'
  | 'caution'
  | 'definition'
  | 'proof'
  | 'answer-space'
  | 'exercise-only';

/** A stretch of the source: `[from, to)`, and the text there. */
export interface Span {
  from: number;
  to: number;
  text: string;
}

/**
 * An editable piece of text inside a node, as a form field shows it.
 *
 * `from`/`to` exclude the whitespace around the content; `text` is that content with
 * the common indentation removed, which `setField` puts back. `spanFrom`/`spanTo` is
 * the whole slot, whitespace included (the inside of `[…]`, say), which an empty field
 * is written into.
 */
export interface Field {
  from: number;
  to: number;
  text: string;
  indent: string;
  spanFrom: number;
  spanTo: number;
  /** the slot's layout: content on lines of its own, rather than inside `[…]` on one line */
  multiline: boolean;
  /** indentation of the line the enclosing call starts on, for the closing bracket */
  outerIndent: string;
}

interface NodeBase {
  from: number;
  to: number;
  /** the node's exact source, `source.slice(from, to)` */
  text: string;
}

export interface StatementNode extends NodeBase {
  kind: 'statement';
  body: Field;
}

export interface SolutionNode extends NodeBase {
  kind: 'solution';
  /** `answer` is Studio's older name; it is read, and kept when editing */
  name: 'solution' | 'answer';
  body: Field;
}

export interface MarkingNode extends NodeBase {
  kind: 'marking';
  body: Field;
}

export interface SubtaskNode extends NodeBase {
  kind: 'subtask';
  /** null when the argument is not a plain number */
  points: number | null;
  args: Span;
  /** the inside of the `[…]` */
  content: Span;
  /** the task text: the content up to its trailing solution and marking */
  body: Field;
  solution: SolutionNode | null;
  marking: MarkingNode | null;
  /** the content parsed as nodes, with absolute ranges */
  children: ExerciseNode[];
}

export interface BlockNode extends NodeBase {
  kind: 'block';
  block: BlockKind;
  /** the function as written: `hint-box` reads as a hint */
  name: string;
  args: Span | null;
  /** a second content block before the body, `#definition[Name][…]` */
  title: Field | null;
  body: Field;
}

export interface ChoiceOption {
  label: Field;
  labelKind: 'content' | 'string';
  correct: boolean;
  /** the `true`/`false` as written */
  correctSpan: Span;
}

export interface ChoicesNode extends NodeBase {
  kind: 'choices';
  layout: string | null;
  columns: string | null;
  options: ChoiceOption[];
}

export interface TrueFalseNode extends NodeBase {
  kind: 'trueFalse';
  correct: boolean;
  correctSpan: Span;
  labels: [string, string] | null;
}

export interface SolutionBoxNode extends NodeBase {
  kind: 'solutionBox';
  width: string | null;
  body: Field;
}

export interface CodeNode extends NodeBase {
  kind: 'code';
  lang: string;
  /** the code between the fences */
  body: Field;
}

export interface RawNode extends NodeBase {
  kind: 'raw';
  reason: string;
}

export type ExerciseNode =
  | StatementNode
  | SubtaskNode
  | BlockNode
  | SolutionNode
  | MarkingNode
  | ChoicesNode
  | TrueFalseNode
  | SolutionBoxNode
  | CodeNode
  | RawNode;

export interface ExerciseModel {
  nodes: ExerciseNode[];
}

/** One text change against the source the model was parsed from. */
export interface TextChange {
  from: number;
  to: number;
  insert: string;
}

/** A node described without positions: what `insertNode`/`replaceNode` write. */
export type NodeSpec =
  | { kind: 'statement'; text: string }
  | { kind: 'subtask'; points: number | null; body: string; solution?: string | null; marking?: string | null }
  | { kind: 'block'; block: BlockKind; name?: string; args?: string | null; title?: string | null; body: string }
  | { kind: 'solution'; body: string; name?: 'solution' | 'answer' }
  | { kind: 'marking'; body: string }
  | {
      kind: 'choices';
      layout?: string | null;
      columns?: string | null;
      options: { label: string; correct: boolean; labelKind?: 'content' | 'string' }[];
    }
  | { kind: 'trueFalse'; correct: boolean; labels?: [string, string] | null }
  | { kind: 'solutionBox'; width?: string | null; body: string }
  | { kind: 'code'; lang: string; text: string }
  | { kind: 'raw'; text: string; reason?: string };

/* ----------------------------------------------------------------- scanner */

/**
 * Thrown by the scanners when a construct is never closed. The parser catches it and
 * turns everything from the line it started on into a raw node.
 */
class Unterminated {
  constructor(
    readonly at: number,
    readonly what: string,
  ) {}
}

const isIdentStart = (c: string | undefined): boolean => c !== undefined && /[A-Za-z_\u00C0-\uFFFF]/.test(c);
const isIdentChar = (c: string | undefined): boolean => c !== undefined && /[A-Za-z0-9_\-\u00C0-\uFFFF]/.test(c);

const KEYWORDS = new Set([
  'let',
  'set',
  'show',
  'import',
  'include',
  'if',
  'for',
  'while',
  'context',
  'return',
  'break',
  'continue',
]);

interface CallPart {
  type: 'args' | 'content';
  /** inside the delimiters */
  from: number;
  to: number;
}

interface HashExpr {
  end: number;
  /** identifier (with field access) for a call or variable; '' for other expressions */
  name: string;
  keyword: boolean;
  /** `#{…}` or `#(…)`: code the form cannot represent */
  code: boolean;
  parts: CallPart[];
}

/** Raw span (backticks) starting at `j`; returns the index just past it. */
function scanRaw(src: string, j: number): number {
  let n = 0;
  while (src[j + n] === '`') n++;
  if (n === 2) return j + 2; // `` is an empty raw span
  if (n === 1) {
    const close = src.indexOf('`', j + 1);
    if (close < 0) throw new Unterminated(j, 'raw text');
    return close + 1;
  }
  const fence = '`'.repeat(n);
  const close = src.indexOf(fence, j + n);
  if (close < 0) throw new Unterminated(j, 'code block');
  // a longer run closes too; its extra backticks belong to the fence
  let end = close + n;
  while (src[end] === '`') end++;
  return end;
}

function scanBlockComment(src: string, j: number): number {
  let depth = 0;
  for (let k = j; k < src.length - 1; k++) {
    if (src[k] === '/' && src[k + 1] === '*') {
      depth++;
      k++;
    } else if (src[k] === '*' && src[k + 1] === '/') {
      depth--;
      k++;
      if (depth === 0) return k + 1;
    }
  }
  throw new Unterminated(j, 'comment');
}

function lineEnd(src: string, j: number): number {
  const nl = src.indexOf('\n', j);
  return nl < 0 ? src.length : nl;
}

function scanString(src: string, j: number): number {
  for (let k = j + 1; k < src.length; k++) {
    if (src[k] === '\\') {
      k++;
      continue;
    }
    if (src[k] === '"') return k + 1;
  }
  throw new Unterminated(j, 'string');
}

/** `https://…` in markup is a link, not a comment. */
function linkStartsAt(src: string, j: number): boolean {
  return /https?:$/.test(src.slice(Math.max(0, j - 6), j));
}

function scanLink(src: string, j: number): number {
  let k = j;
  while (k < src.length && !/[\s\[\]<>"]/.test(src[k]!)) k++;
  return k;
}

/** Math from the `$` at `j`; returns the index just past the closing `$`. */
function scanMath(src: string, j: number): number {
  let k = j + 1;
  while (k < src.length) {
    const c = src[k]!;
    if (c === '\\') k += 2;
    else if (c === '$') return k + 1;
    else if (c === '"') k = scanString(src, k);
    else if (c === '/' && src[k + 1] === '/') k = lineEnd(src, k);
    else if (c === '/' && src[k + 1] === '*') k = scanBlockComment(src, k);
    else if (c === '#') k = scanHash(src, k).end;
    else k++;
  }
  throw new Unterminated(j, 'formula');
}

/**
 * Code from just inside an opening `(`/`{`/`[`-free code position; returns the index of
 * the matching `closer`. Content blocks inside are scanned as markup.
 */
function scanCode(src: string, j: number, closer: ')' | '}', opened: number): number {
  const stack: string[] = [closer];
  let k = j;
  while (k < src.length) {
    const c = src[k]!;
    if (c === '"') k = scanString(src, k);
    else if (c === '/' && src[k + 1] === '/') k = lineEnd(src, k);
    else if (c === '/' && src[k + 1] === '*') k = scanBlockComment(src, k);
    else if (c === '`') k = scanRaw(src, k);
    else if (c === '$') k = scanMath(src, k);
    else if (c === '[') k = scanContent(src, k) + 1;
    else if (c === '(') {
      stack.push(')');
      k++;
    } else if (c === '{') {
      stack.push('}');
      k++;
    } else if (c === ')' || c === '}' || c === ']') {
      if (c !== stack[stack.length - 1]) throw new Unterminated(opened, 'bracket');
      stack.pop();
      if (!stack.length) return k;
      k++;
    } else k++;
  }
  throw new Unterminated(opened, closer === ')' ? 'parenthesis' : 'brace');
}

/** An embedded statement (`#let …`): code up to the end of the line, or a `;`. */
function scanStatement(src: string, j: number): number {
  const stack: string[] = [];
  let k = j;
  while (k < src.length) {
    const c = src[k]!;
    if (!stack.length && (c === '\n' || c === ';')) return k;
    if (c === '"') k = scanString(src, k);
    else if (c === '/' && src[k + 1] === '/') k = lineEnd(src, k);
    else if (c === '/' && src[k + 1] === '*') k = scanBlockComment(src, k);
    else if (c === '`') k = scanRaw(src, k);
    else if (c === '$') k = scanMath(src, k);
    else if (c === '[') k = scanContent(src, k) + 1;
    else if (c === '(' || c === '{') {
      stack.push(c === '(' ? ')' : '}');
      k++;
    } else if (c === ')' || c === '}' || c === ']') {
      if (!stack.length) return k; // closes something outside the statement
      if (c !== stack[stack.length - 1]) throw new Unterminated(j, 'bracket');
      stack.pop();
      k++;
    } else k++;
  }
  if (stack.length) throw new Unterminated(j, 'statement');
  return k;
}

/** The embedded expression at the `#` at `j`. Never consumes less than the `#`. */
function scanHash(src: string, j: number): HashExpr {
  let k = j + 1;
  const c = src[k];
  const parts: CallPart[] = [];
  if (c === '(') {
    const close = scanCode(src, k + 1, ')', k);
    return { end: close + 1, name: '', keyword: false, code: true, parts };
  }
  if (c === '{') {
    const close = scanCode(src, k + 1, '}', k);
    return { end: close + 1, name: '', keyword: false, code: true, parts };
  }
  if (c === '[') {
    const close = scanContent(src, k);
    return { end: close + 1, name: '', keyword: false, code: false, parts: [{ type: 'content', from: k + 1, to: close }] };
  }
  if (c === '"') return { end: scanString(src, k), name: '', keyword: false, code: false, parts };
  if (!isIdentStart(c)) return { end: j + 1, name: '', keyword: false, code: false, parts };
  let name = '';
  while (isIdentChar(src[k])) name += src[k++];
  if (KEYWORDS.has(name)) {
    return { end: scanStatement(src, k), name, keyword: true, code: true, parts };
  }
  for (;;) {
    if (src[k] === '.' && isIdentStart(src[k + 1])) {
      name += src[k++];
      while (isIdentChar(src[k])) name += src[k++];
    } else if (src[k] === '(') {
      const close = scanCode(src, k + 1, ')', k);
      parts.push({ type: 'args', from: k + 1, to: close });
      k = close + 1;
    } else if (src[k] === '[') {
      const close = scanContent(src, k);
      parts.push({ type: 'content', from: k + 1, to: close });
      k = close + 1;
    } else break;
  }
  /*
   * An identifier in markup ends before a trailing hyphen: Typst reads `#x-` as `#x`
   * followed by text. Only the identifier itself matters here, so nothing is lost.
   */
  return { end: k, name, keyword: false, code: false, parts };
}

/** One markup token at `j` other than a bracket; returns the index just past it. */
function stepMarkup(src: string, j: number): number {
  const c = src[j]!;
  if (c === '\\') return Math.min(src.length, j + 2);
  if (c === '`') return scanRaw(src, j);
  if (c === '$') return scanMath(src, j);
  if (c === '#') return scanHash(src, j).end;
  if (c === '/' && src[j + 1] === '/') return linkStartsAt(src, j) ? scanLink(src, j) : lineEnd(src, j);
  if (c === '/' && src[j + 1] === '*') return scanBlockComment(src, j);
  return j + 1;
}

/** The `]` matching the `[` at `open`. */
function scanContent(src: string, open: number): number {
  let depth = 1;
  let k = open + 1;
  while (k < src.length) {
    const c = src[k]!;
    if (c === '[') {
      depth++;
      k++;
    } else if (c === ']') {
      if (--depth === 0) return k;
      k++;
    } else k = stepMarkup(src, k);
  }
  throw new Unterminated(open, 'bracket');
}

/* ------------------------------------------------------------------ fields */

const leadingWs = (s: string) => /^\s*/.exec(s)![0];
const trailingWs = (s: string) => /\s*$/.exec(s)![0];

function lineStartOf(src: string, at: number): number {
  return src.lastIndexOf('\n', at - 1) + 1;
}

/** Leading spaces and tabs of the line `at` is on. */
function lineIndent(src: string, at: number): string {
  const ls = lineStartOf(src, at);
  return /^[ \t]*/.exec(src.slice(ls, at))![0];
}

function commonPrefix(a: string, b: string): string {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return a.slice(0, i);
}

function makeField(src: string, spanFrom: number, spanTo: number, outerIndent: string): Field {
  const span = src.slice(spanFrom, spanTo);
  const lead = leadingWs(span);
  const multiline = lead.includes('\n');
  if (lead.length === span.length) {
    return {
      from: spanTo,
      to: spanTo,
      text: '',
      indent: outerIndent + '  ',
      spanFrom,
      spanTo,
      multiline,
      outerIndent,
    };
  }
  const from = spanFrom + lead.length;
  const to = spanTo - trailingWs(span).length;
  const inner = src.slice(from, to);
  const lines = inner.split('\n');
  // the first line's own indentation counts when nothing else stands before it
  const firstPrefix = src.slice(lineStartOf(src, from), from);
  let indent: string | null = /^[ \t]*$/.test(firstPrefix) ? firstPrefix : null;
  for (const line of lines.slice(1)) {
    if (!line.trim()) continue;
    const own = /^[ \t]*/.exec(line)![0];
    indent = indent === null ? own : commonPrefix(indent, own);
  }
  if (indent === null) indent = outerIndent + '  ';
  const text = lines
    .map((line, i) => {
      if (i === 0) return line;
      if (!line.trim()) return '';
      return line.startsWith(indent!) ? line.slice(indent!.length) : line.replace(/^[ \t]*/, '');
    })
    .join('\n');
  return { from, to, text, indent, spanFrom, spanTo, multiline, outerIndent };
}

/** `text` with every line after the first indented by `indent`; blank lines stay empty. */
function reindent(text: string, indent: string): string {
  return text
    .split('\n')
    .map((line, i) => (i === 0 || !line.trim() ? (i === 0 ? line : '') : indent + line))
    .join('\n');
}

/** The change turning `oldText` at `from` into `newText`, trimmed to what differs. */
export function minimalChange(from: number, oldText: string, newText: string): TextChange {
  let p = 0;
  const max = Math.min(oldText.length, newText.length);
  while (p < max && oldText[p] === newText[p]) p++;
  let s = 0;
  while (s < max - p && oldText[oldText.length - 1 - s] === newText[newText.length - 1 - s]) s++;
  return { from: from + p, to: from + oldText.length - s, insert: newText.slice(p, newText.length - s) };
}

/** Apply one change to a string. */
export function applyChange(src: string, change: TextChange): string {
  return src.slice(0, change.from) + change.insert + src.slice(change.to);
}

/* ------------------------------------------------------------------ parser */

const BLOCK_NAMES: Record<string, BlockKind> = {
  hint: 'hint',
  'hint-box': 'hint',
  note: 'note',
  example: 'example',
  caution: 'caution',
  definition: 'definition',
  proof: 'proof',
  'answer-space': 'answer-space',
  'exercise-only': 'exercise-only',
};

const STRUCTURED = new Set([
  ...Object.keys(BLOCK_NAMES),
  'subtask',
  'solution',
  'answer',
  'marking',
  'choices',
  'true-false',
  'solution-box',
]);

/** Positions at which only spaces and tabs stand between the line start and them. */
function lineStarts(src: string): Uint8Array {
  const ok = new Uint8Array(src.length + 1);
  let at = 1;
  for (let i = 0; i <= src.length; i++) {
    ok[i] = at;
    const c = src[i];
    if (c === '\n') at = 1;
    else if (c !== ' ' && c !== '\t' && c !== '\r') at = 0;
  }
  return ok;
}

/** Whether only spaces and tabs stand between `at` and the end of its line (or `limit`). */
function endsLine(src: string, at: number, limit: number): boolean {
  for (let k = at; k < limit; k++) {
    const c = src[k];
    if (c === '\n') return true;
    if (c !== ' ' && c !== '\t' && c !== '\r') return false;
  }
  return true;
}

/** Split a call's argument source at its top-level commas. */
function splitArgs(src: string, from: number, to: number): Span[] {
  const out: Span[] = [];
  let start = from;
  let k = from;
  const stack: string[] = [];
  while (k < to) {
    const c = src[k]!;
    if (c === '"') k = scanString(src, k);
    else if (c === '/' && src[k + 1] === '/') k = lineEnd(src, k);
    else if (c === '/' && src[k + 1] === '*') k = scanBlockComment(src, k);
    else if (c === '`') k = scanRaw(src, k);
    else if (c === '$') k = scanMath(src, k);
    else if (c === '[') k = scanContent(src, k) + 1;
    else if (c === '(' || c === '{') {
      stack.push(c);
      k++;
    } else if (c === ')' || c === '}') {
      stack.pop();
      k++;
    } else if (c === ',' && !stack.length) {
      out.push({ from: start, to: k, text: src.slice(start, k) });
      start = ++k;
    } else k++;
  }
  if (src.slice(start, to).trim()) out.push({ from: start, to, text: src.slice(start, to) });
  return out;
}

/** A Span trimmed of surrounding whitespace. */
function trimSpan(src: string, s: Span): Span {
  const from = s.from + leadingWs(s.text).length;
  const to = Math.max(from, s.to - trailingWs(s.text).length);
  return { from, to, text: src.slice(from, to) };
}

const NUMBER = /^-?\d+(\.\d+)?$/;
const BOOL = /^(true|false)$/;

/** `name: value` → [name, value span]; positional → [null, span]. */
function namedArg(src: string, s: Span): [string | null, Span] {
  const t = trimSpan(src, s);
  const m = /^([A-Za-z_][A-Za-z0-9_-]*)\s*:\s*/.exec(t.text);
  if (!m) return [null, t];
  const from = t.from + m[0].length;
  return [m[1]!, { from, to: t.to, text: src.slice(from, t.to) }];
}

/** `[content]` or `"string"` as a label field, or null. */
function labelField(src: string, s: Span, outerIndent: string): { field: Field; kind: 'content' | 'string' } | null {
  const t = trimSpan(src, s);
  if (t.text.startsWith('[')) {
    let close: number;
    try {
      close = scanContent(src, t.from);
    } catch {
      return null;
    }
    if (close !== t.to - 1) return null;
    return { field: makeField(src, t.from + 1, close, outerIndent), kind: 'content' };
  }
  if (t.text.startsWith('"')) {
    let end: number;
    try {
      end = scanString(src, t.from);
    } catch {
      return null;
    }
    if (end !== t.to) return null;
    // a string is whitespace-sensitive and holds escapes: the field is its exact inside
    const field: Field = {
      from: t.from + 1,
      to: t.to - 1,
      text: src.slice(t.from + 1, t.to - 1),
      indent: '',
      spanFrom: t.from + 1,
      spanTo: t.to - 1,
      multiline: false,
      outerIndent,
    };
    return { field, kind: 'string' };
  }
  return null;
}

/** A tuple `(label, bool)` as a choice option, or null. */
function choiceOption(src: string, s: Span, outerIndent: string): ChoiceOption | null {
  const t = trimSpan(src, s);
  if (!t.text.startsWith('(') || !t.text.endsWith(')')) return null;
  let close: number;
  try {
    close = scanCode(src, t.from + 1, ')', t.from);
  } catch {
    return null;
  }
  if (close !== t.to - 1) return null;
  const items = splitArgs(src, t.from + 1, close);
  if (items.length !== 2) return null;
  const label = labelField(src, items[0]!, outerIndent);
  const correct = trimSpan(src, items[1]!);
  if (!label || !BOOL.test(correct.text)) return null;
  return { label: label.field, labelKind: label.kind, correct: correct.text === 'true', correctSpan: correct };
}

type Built = ExerciseNode | { raw: string };

/**
 * The node for a recognised call, or `{raw: reason}` when the name is known but the
 * shape is not one the form can edit.
 */
function buildCall(src: string, start: number, expr: HashExpr, ok: Uint8Array): Built {
  const { name, parts } = expr;
  const base = { from: start, to: expr.end, text: src.slice(start, expr.end) };
  const outer = lineIndent(src, start);
  const args = parts.filter((p) => p.type === 'args');
  const contents = parts.filter((p) => p.type === 'content');
  const shape = parts.map((p) => (p.type === 'args' ? 'a' : 'c')).join('');
  const argSpan = (p: CallPart): Span => ({ from: p.from, to: p.to, text: src.slice(p.from, p.to) });

  if (name === 'solution' || name === 'answer') {
    if (shape !== 'c') return { raw: `#${name} with arguments` };
    return { ...base, kind: 'solution', name, body: makeField(src, contents[0]!.from, contents[0]!.to, outer) };
  }
  if (name === 'marking') {
    if (shape !== 'c') return { raw: '#marking with arguments' };
    return { ...base, kind: 'marking', body: makeField(src, contents[0]!.from, contents[0]!.to, outer) };
  }
  if (name === 'subtask') {
    if (shape !== 'ac') return { raw: '#subtask without (points)[…]' };
    const a = argSpan(args[0]!);
    const t = a.text.trim();
    const points = NUMBER.test(t) ? Number(t) : null;
    const c = contents[0]!;
    const children = parseRange(src, c.from, c.to, ok);
    // the trailing run of solution and marking, at most one of each
    let solution: SolutionNode | null = null;
    let marking: MarkingNode | null = null;
    let bodyEnd = c.to;
    for (let i = children.length - 1; i >= 0; i--) {
      const n = children[i]!;
      if (n.kind === 'statement' && !n.text.trim()) continue;
      if (n.kind === 'solution' && !solution) solution = n;
      else if (n.kind === 'marking' && !marking) marking = n;
      else break;
      bodyEnd = n.from;
    }
    // the whitespace before the run belongs to the body slot, not to the solution
    return {
      ...base,
      kind: 'subtask',
      points,
      args: a,
      content: { from: c.from, to: c.to, text: src.slice(c.from, c.to) },
      body: makeField(src, c.from, bodyEnd, outer),
      solution,
      marking,
      children,
    };
  }
  if (name in BLOCK_NAMES) {
    // [body], (args)[body], [title][body], (args)[title][body]
    if (!/^a?c{1,2}$/.test(shape)) return { raw: `#${name} with an unusual shape` };
    const body = contents[contents.length - 1]!;
    const title = contents.length === 2 ? contents[0]! : null;
    return {
      ...base,
      kind: 'block',
      block: BLOCK_NAMES[name]!,
      name,
      args: args[0] ? argSpan(args[0]) : null,
      title: title ? makeField(src, title.from, title.to, outer) : null,
      body: makeField(src, body.from, body.to, outer),
    };
  }
  if (name === 'solution-box') {
    if (shape !== 'c' && shape !== 'ac') return { raw: '#solution-box with an unusual shape' };
    let width: string | null = null;
    if (args[0]) {
      const items = splitArgs(src, args[0].from, args[0].to);
      for (const item of items) {
        const [key, value] = namedArg(src, item);
        if (key !== 'width') return { raw: '#solution-box with arguments other than width' };
        width = value.text;
      }
    }
    return { ...base, kind: 'solutionBox', width, body: makeField(src, contents[0]!.from, contents[0]!.to, outer) };
  }
  if (name === 'true-false') {
    if (shape !== 'a') return { raw: '#true-false with an unusual shape' };
    const items = splitArgs(src, args[0]!.from, args[0]!.to);
    let correctSpan: Span | null = null;
    let labels: [string, string] | null = null;
    for (const item of items) {
      const [key, value] = namedArg(src, item);
      if (key === null && !correctSpan && BOOL.test(value.text)) correctSpan = value;
      else if (key === 'labels') {
        const pair = value.text.startsWith('(') && value.text.endsWith(')')
          ? splitArgs(src, value.from + 1, value.to - 1)
          : [];
        const fields = pair.map((p) => labelField(src, p, outer));
        if (fields.length !== 2 || fields.some((f) => !f || f.kind !== 'content')) {
          return { raw: '#true-false labels the form cannot edit' };
        }
        labels = [fields[0]!.field.text, fields[1]!.field.text];
      } else return { raw: '#true-false with arguments the form cannot edit' };
    }
    if (!correctSpan) return { raw: '#true-false without true or false' };
    return { ...base, kind: 'trueFalse', correct: correctSpan.text === 'true', correctSpan, labels };
  }
  if (name === 'choices') {
    if (shape !== 'a') return { raw: '#choices with an unusual shape' };
    const items = splitArgs(src, args[0]!.from, args[0]!.to);
    let layout: string | null = null;
    let columns: string | null = null;
    const options: ChoiceOption[] = [];
    for (const item of items) {
      const [key, value] = namedArg(src, item);
      if (key === 'layout') {
        if (!/^"[a-z]+"$/.test(value.text)) return { raw: '#choices layout the form cannot edit' };
        layout = value.text.slice(1, -1);
      } else if (key === 'columns') columns = value.text;
      else if (key === null) {
        const option = choiceOption(src, value, outer);
        if (!option) return { raw: '#choices option the form cannot edit' };
        options.push(option);
      } else return { raw: '#choices with arguments the form cannot edit' };
    }
    return { ...base, kind: 'choices', layout, columns, options };
  }
  return { raw: `#${name}` };
}

/** Code fence at line start `j`, as a node, or null when it is not a standalone block. */
function buildFence(src: string, j: number, end: number): CodeNode | null {
  let n = 0;
  while (src[j + n] === '`') n++;
  let k = j + n;
  let lang = '';
  while (k < end && /[A-Za-z0-9_+.#-]/.test(src[k]!)) lang += src[k++];
  // the code proper: after the language tag up to the closing fence
  let close = end - n;
  while (src[close - 1] === '`') close--;
  const outer = lineIndent(src, j);
  return {
    from: j,
    to: end,
    text: src.slice(j, end),
    kind: 'code',
    lang,
    body: makeField(src, k, close, outer),
  };
}

const statement = (src: string, from: number, to: number): StatementNode => ({
  from,
  to,
  text: src.slice(from, to),
  kind: 'statement',
  body: makeField(src, from, to, ''),
});

const rawNode = (src: string, from: number, to: number, reason: string): RawNode => ({
  from,
  to,
  text: src.slice(from, to),
  kind: 'raw',
  reason,
});

/** Parse `src[from, to)` into nodes covering it exactly. */
function parseRange(src: string, from: number, to: number, ok: Uint8Array): ExerciseNode[] {
  const nodes: ExerciseNode[] = [];
  let stmt = from;
  let depth = 0;
  let j = from;
  const flush = (upto: number) => {
    if (upto > stmt) nodes.push(statement(src, stmt, upto));
    stmt = upto;
  };
  while (j < to) {
    const c = src[j]!;
    try {
      if (ok[j] && (c === '#' || (c === '`' && src.startsWith('```', j)))) {
        if (c === '`') {
          const end = scanRaw(src, j);
          if (end <= to && endsLine(src, end, to)) {
            flush(j);
            nodes.push(buildFence(src, j, end)!);
            j = stmt = end;
            continue;
          }
          j = end;
          continue;
        }
        const expr = scanHash(src, j);
        if (expr.end > to) throw new Unterminated(j, 'bracket');
        const standalone = endsLine(src, expr.end, to);
        if (standalone && STRUCTURED.has(expr.name)) {
          const built = buildCall(src, j, expr, ok);
          flush(j);
          nodes.push('raw' in built ? rawNode(src, j, expr.end, built.raw) : built);
          j = stmt = expr.end;
          continue;
        }
        const multiline = src.slice(j, expr.end).includes('\n');
        if (standalone && (expr.keyword || expr.code || (expr.name && multiline))) {
          flush(j);
          const reason = expr.keyword
            ? `#${expr.name}`
            : expr.code
              ? 'code'
              : `#${expr.name} (no form for it)`;
          nodes.push(rawNode(src, j, expr.end, reason));
          j = stmt = expr.end;
          continue;
        }
        j = Math.max(expr.end, j + 1);
        continue;
      }
      if (c === '[') {
        depth++;
        j++;
        continue;
      }
      if (c === ']') {
        if (depth > 0) {
          depth--;
          j++;
          continue;
        }
        // a closing bracket with nothing to close: typst refuses it, the form keeps it
        const ls = Math.max(stmt, lineStartOf(src, j));
        const le = Math.min(to, lineEnd(src, j));
        flush(ls);
        nodes.push(rawNode(src, ls, le, 'unmatched ]'));
        j = stmt = le;
        continue;
      }
      j = stepMarkup(src, j);
    } catch (err) {
      if (!(err instanceof Unterminated)) throw err;
      const ls = Math.max(stmt, lineStartOf(src, Math.min(err.at, j)));
      flush(ls);
      nodes.push(rawNode(src, ls, to, `unclosed ${err.what}`));
      return nodes;
    }
  }
  /*
   * A token may run past `to` — a raw span or comment whose end lies beyond the range
   * being parsed. Only possible for a sub-range, never for the whole source, and then
   * the remainder is still one statement: the node list must cover the range exactly.
   */
  flush(to);
  return nodes;
}

/** Read a Typst exercise body. Total: never throws, and always lossless. */
export function parseExercise(src: string): ExerciseModel {
  return { nodes: parseRange(src, 0, src.length, lineStarts(src)) };
}

/** The source again: every node's text, in order. */
export function serializeExercise(model: ExerciseModel): string {
  return model.nodes.map((n) => n.text).join('');
}

/* ----------------------------------------------------------------- queries */

/** A statement of nothing but whitespace: the gap between two parts, hidden by the form. */
export function isBlank(node: ExerciseNode): boolean {
  return node.kind === 'statement' && !node.text.trim();
}

/** Sum of the subtasks' points, or null when there are no subtasks. */
export function totalPoints(model: ExerciseModel): number | null {
  const subtasks = model.nodes.filter((n): n is SubtaskNode => n.kind === 'subtask');
  if (!subtasks.length) return null;
  const sum = subtasks.reduce((s, n) => s + (n.points ?? 0), 0);
  return Math.round(sum * 1000) / 1000;
}

/** Offset of the start of 1-based `line`. */
export function lineOffset(src: string, line: number): number {
  let at = 0;
  for (let l = 1; l < line; l++) {
    const nl = src.indexOf('\n', at);
    if (nl < 0) return src.length;
    at = nl + 1;
  }
  return at;
}

/**
 * Index of the node a diagnostic belongs to: the one containing the offset, skipping
 * a blank gap in favour of the node after it. -1 for an empty model.
 */
export function nodeIndexAt(model: ExerciseModel, offset: number): number {
  const { nodes } = model;
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    if (offset >= n.from && offset < n.to) {
      if (isBlank(n) && i + 1 < nodes.length) return i + 1;
      return i;
    }
  }
  return nodes.length - 1;
}

/** A parsed node described without positions, as `insertNode` would write it. */
export function toSpec(node: ExerciseNode): NodeSpec {
  switch (node.kind) {
    case 'statement':
      return { kind: 'statement', text: node.body.text };
    case 'subtask':
      return {
        kind: 'subtask',
        points: node.points,
        body: node.body.text,
        solution: node.solution ? node.solution.body.text : null,
        marking: node.marking ? node.marking.body.text : null,
      };
    case 'block':
      return {
        kind: 'block',
        block: node.block,
        name: node.name,
        args: node.args ? node.args.text : null,
        title: node.title ? node.title.text : null,
        body: node.body.text,
      };
    case 'solution':
      return { kind: 'solution', body: node.body.text, name: node.name };
    case 'marking':
      return { kind: 'marking', body: node.body.text };
    case 'choices':
      return {
        kind: 'choices',
        layout: node.layout,
        columns: node.columns,
        options: node.options.map((o) => ({
          label: o.labelKind === 'string' ? unescapeString(o.label.text) : o.label.text,
          correct: o.correct,
          labelKind: o.labelKind,
        })),
      };
    case 'trueFalse':
      return { kind: 'trueFalse', correct: node.correct, labels: node.labels };
    case 'solutionBox':
      return { kind: 'solutionBox', width: node.width, body: node.body.text };
    case 'code':
      return { kind: 'code', lang: node.lang, text: node.body.text };
    case 'raw':
      return { kind: 'raw', text: node.text, reason: node.reason };
  }
}

/* ------------------------------------------------------------------ writer */

/** Content inside `[…]`: on one line when short, else on lines of its own. */
function bracket(body: string, indent: string, forceBlock = false): string {
  const t = body.trim();
  // a line comment would swallow the closing bracket, a trailing backslash escape it
  const oneLine =
    !forceBlock && !t.includes('\n') && t.length <= 72 && !/\\$/.test(t) && !t.includes('//') && !t.includes('```');
  if (oneLine) return `[${t}]`;
  if (!t) return '[]';
  const inner = indent + '  ';
  return `[\n${inner}${reindent(t, inner)}\n${indent}]`;
}

/** A Typst string literal for `text`. */
function escapeString(text: string): string {
  return '"' + text.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\t/g, '\\t') + '"';
}

/** The text a Typst string literal's inside stands for. */
function unescapeString(inner: string): string {
  return inner.replace(/\\(u\{([0-9A-Fa-f]+)\}|.)/g, (_m, esc: string, hex?: string) => {
    if (hex) return String.fromCodePoint(parseInt(hex, 16));
    return esc === 'n' ? '\n' : esc === 't' ? '\t' : esc === 'r' ? '\r' : esc;
  });
}

/** A plain number as Typst writes it. */
const num = (n: number | null): string => String(n ?? 0);

/** Canonical source for a node, its first line at `indent`. */
export function nodeSource(spec: NodeSpec, indent = ''): string {
  switch (spec.kind) {
    case 'statement':
      return reindent(spec.text, indent);
    case 'raw':
      return spec.text;
    case 'solution':
      return `#${spec.name ?? 'solution'}${bracket(spec.body, indent)}`;
    case 'marking':
      return `#marking${bracket(spec.body, indent)}`;
    case 'subtask': {
      const inner = indent + '  ';
      const hasParts = spec.solution != null || spec.marking != null;
      if (!hasParts) return `#subtask(${num(spec.points)})${bracket(spec.body, indent)}`;
      const lines = [`#subtask(${num(spec.points)})[`];
      if (spec.body.trim()) lines.push(inner + reindent(spec.body.trim(), inner), '');
      if (spec.solution != null) lines.push(inner + nodeSource({ kind: 'solution', body: spec.solution }, inner));
      if (spec.marking != null) lines.push(inner + nodeSource({ kind: 'marking', body: spec.marking }, inner));
      lines.push(`${indent}]`);
      return lines.join('\n');
    }
    case 'block': {
      const name = spec.name ?? spec.block;
      const args = spec.args != null ? `(${spec.args})` : '';
      const title = spec.title != null ? `[${spec.title.trim()}]` : '';
      return `#${name}${args}${title}${bracket(spec.body, indent)}`;
    }
    case 'solutionBox':
      return `#solution-box${spec.width ? `(width: ${spec.width})` : ''}${bracket(spec.body, indent)}`;
    case 'trueFalse': {
      const labels = spec.labels ? `, labels: ([${spec.labels[0]}], [${spec.labels[1]}])` : '';
      return `#true-false(${spec.correct ? 'true' : 'false'}${labels})`;
    }
    case 'choices': {
      const inner = indent + '  ';
      const args: string[] = [];
      if (spec.layout) args.push(`layout: "${spec.layout}"`);
      if (spec.columns) args.push(`columns: ${spec.columns}`);
      for (const o of spec.options) {
        const label = o.labelKind === 'string' ? escapeString(o.label) : `[${o.label.trim()}]`;
        args.push(`(${label}, ${o.correct ? 'true' : 'false'})`);
      }
      if (!args.length) return '#choices()';
      return `#choices(\n${args.map((a) => `${inner}${a},`).join('\n')}\n${indent})`;
    }
    case 'code': {
      // a fence longer than any backtick run inside the code
      const longest = Math.max(2, ...(spec.text.match(/`+/g) ?? []).map((r) => r.length));
      const fence = '`'.repeat(longest + 1);
      return `${fence}${spec.lang}\n${indent}${reindent(spec.text, indent)}\n${indent}${fence}`;
    }
  }
}

/** A whole body from specs, parts separated by blank lines. */
export function buildExercise(specs: NodeSpec[]): string {
  return specs.map((s) => nodeSource(s)).join('\n\n') + (specs.length ? '\n' : '');
}

/* ----------------------------------------------------------------- editing */

/**
 * The change that sets a field to `text` (as the form shows it, without the
 * indentation). An empty field is written into its slot with the slot's layout; a
 * field emptied by the user collapses its slot to nothing.
 */
export function setField(src: string, field: Field, text: string): TextChange {
  const t = text.replace(/\s+$/, '').replace(/^\s*\n/, '');
  if (!t.trim()) {
    return minimalChange(field.spanFrom, src.slice(field.spanFrom, field.spanTo), '');
  }
  if (field.from === field.to) {
    const multi = field.multiline || t.includes('\n');
    const written = multi
      ? `\n${field.indent}${reindent(t, field.indent)}\n${field.outerIndent}`
      : t;
    return minimalChange(field.spanFrom, src.slice(field.spanFrom, field.spanTo), written);
  }
  let written = reindent(t, field.indent);
  // a single-line slot that becomes several lines gets the brackets on lines of their own
  if (!field.multiline && t.includes('\n') && !src.slice(field.spanFrom, field.from).includes('\n')) {
    written = `\n${field.indent}${written}\n${field.outerIndent}`;
    return minimalChange(field.spanFrom, src.slice(field.spanFrom, field.spanTo), written);
  }
  return minimalChange(field.from, src.slice(field.from, field.to), written);
}

/** The node's main text: a statement, a block's body, a subtask's task, a code block. */
export function primaryField(node: ExerciseNode): Field | null {
  switch (node.kind) {
    case 'statement':
    case 'subtask':
    case 'block':
    case 'solution':
    case 'marking':
    case 'solutionBox':
    case 'code':
      return node.body;
    default:
      return null;
  }
}

/** Set a node's main text (see `primaryField`); a raw node is replaced verbatim. */
export function replaceNodeBody(src: string, node: ExerciseNode, newBody: string): TextChange {
  if (node.kind === 'raw') return minimalChange(node.from, node.text, newBody);
  const field = primaryField(node);
  // choices and true/false have no text of their own: edit them through `replaceNode`
  if (!field) return { from: node.from, to: node.from, insert: '' };
  return setField(src, field, newBody);
}

/** Rewrite a node from a spec, as one minimal change inside the node's range. */
export function replaceNode(src: string, node: ExerciseNode, spec: NodeSpec): TextChange {
  const written = nodeSource(spec, lineIndent(src, node.from));
  return minimalChange(node.from, src.slice(node.from, node.to), written);
}

/** Set a subtask's points. */
export function setSubtaskPoints(src: string, node: SubtaskNode, points: number): TextChange {
  return minimalChange(node.args.from, node.args.text, num(points));
}

/**
 * Set, add or remove (`null`) a subtask's solution or marking.
 *
 * An added part goes on a line of its own after the task — the solution before the
 * marking — and a subtask written on one line opens up so its closing bracket gets a
 * line of its own too.
 */
export function setSubtaskPart(
  src: string,
  node: SubtaskNode,
  part: 'solution' | 'marking',
  text: string | null,
): TextChange {
  const existing = part === 'solution' ? node.solution : node.marking;
  if (existing) {
    if (text === null) {
      // the part and the whitespace before it, back to the previous content
      let from = existing.from;
      while (from > node.content.from && /\s/.test(src[from - 1]!)) from--;
      return minimalChange(from, src.slice(from, existing.to), '');
    }
    return setField(src, existing.body, text);
  }
  if (text === null) return { from: node.from, to: node.from, insert: '' };
  const outer = lineIndent(src, node.from);
  const inner = outer + '  ';
  const written = nodeSource(part === 'solution' ? { kind: 'solution', body: text } : { kind: 'marking', body: text }, inner);
  // after the task text — or after the solution, for a marking — and so before a marking
  const afterSolution = part === 'marking' && node.solution !== null;
  const emptyBody = node.body.from === node.body.to;
  const pos = afterSolution ? node.solution!.to : emptyBody ? node.content.from : node.body.to;
  const lead = afterSolution || pos === node.content.from ? '\n' : '\n\n';
  const rest = src.slice(pos, node.content.to);
  if (rest.includes('\n')) return { from: pos, to: pos, insert: `${lead}${inner}${written}` };
  // the closing bracket sits on this line: it moves to a line of its own
  return minimalChange(pos, rest, `${lead}${inner}${written}\n${outer}`);
}

/** The separators that put `text` on lines of its own between `before` and `after`. */
function standalone(before: string, text: string, after: string): string {
  const pre = !before ? '' : before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
  const post = !after ? '\n' : after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';
  return pre + text + post;
}

/**
 * Insert a node before `model.nodes[at]` (or at the end), on lines of its own and
 * separated from its neighbours by a blank line.
 */
export function insertNode(src: string, model: ExerciseModel, at: number, spec: NodeSpec): TextChange {
  const pos = at >= model.nodes.length ? src.length : model.nodes[at]!.from;
  const text = nodeSource(spec, lineIndent(src, pos));
  return { from: pos, to: pos, insert: standalone(src.slice(0, pos), text, src.slice(pos)) };
}

/** Remove a node and the gap it leaves, keeping one paragraph break between neighbours. */
export function removeNode(src: string, node: ExerciseNode): TextChange {
  // a statement's range holds the gaps around it; only its text goes
  const s = node.from + leadingWs(node.text).length;
  const e = Math.max(s, node.to - trailingWs(node.text).length);
  if (s === e) return { from: node.from, to: node.from, insert: '' };
  let a = s;
  while (a > 0 && /\s/.test(src[a - 1]!)) a--;
  let b = e;
  while (b < src.length && /\s/.test(src[b]!)) b++;
  const gapBefore = src.slice(a, s);
  const gapAfter = src.slice(e, b);
  let joint: string;
  if (a === 0) joint = b === src.length ? '' : (/[ \t]*$/.exec(gapAfter)?.[0] ?? '');
  else if (b === src.length) joint = gapAfter.includes('\n') || gapBefore.includes('\n') ? '\n' : '';
  else {
    const breaks = Math.max((gapBefore.match(/\n/g) ?? []).length, (gapAfter.match(/\n/g) ?? []).length);
    joint = (breaks >= 2 ? '\n\n' : breaks === 1 ? '\n' : ' ') + (/[ \t]*$/.exec(gapAfter)?.[0] ?? '');
  }
  return minimalChange(a, src.slice(a, b), joint);
}

/**
 * Move a node before `before` (null: to the end), as one change.
 *
 * Computed as remove-then-insert on a copy and returned as the single minimal change
 * between the two sources, so an Undo takes the whole move back in one step.
 */
export function moveNode(src: string, node: ExerciseNode, before: ExerciseNode | null): TextChange {
  if (before === node) return { from: node.from, to: node.from, insert: '' };
  const removal = removeNode(src, node);
  const without = applyChange(src, removal);
  const shift = removal.insert.length - (removal.to - removal.from);
  let pos: number;
  if (!before) pos = without.length;
  else if (before.from >= removal.to) pos = before.from + shift;
  else if (before.from <= removal.from) pos = before.from;
  else pos = removal.from;
  // land on a line start: the content of the target line moves down, not apart
  const ls = lineStartOf(without, pos);
  if (/^[ \t]*$/.test(without.slice(ls, pos))) pos = ls;
  const indent = lineIndent(without, pos);
  const text = indent + node.text.trim();
  const moved = without.slice(0, pos) + standalone(without.slice(0, pos), text, without.slice(pos)) + without.slice(pos);
  return minimalChange(0, src, moved);
}

/* ---------------------------------------------------------------- escaping */

/**
 * Plain text as Typst markup that prints it: the characters that open markup —
 * brackets, `#`, `$`, `*`, `_`, backticks, `<`, `@` — get a backslash, as does the
 * backslash itself and the first slash of `//`.
 */
export function escapePlain(text: string): string {
  return text.replace(/([\\\[\]#$*_`<@])/g, '\\$1').replace(/\/(?=\/)/g, '\\/');
}

/** A field edit, as `escapeTyped` reports it. */
export interface TypedEdit {
  /** the field's text to keep */
  text: string;
  /** where the caret belongs afterwards */
  cursor: number;
  /** the text the edit removed from the field (before escaping anything) */
  removed: string;
}

/**
 * A field edit with only the newly typed text escaped.
 *
 * The form's fields hold markup — the toolbar inserts `*bold*` on purpose — but what
 * someone types is text: `a[0]` must not open a content block that swallows the rest
 * of the exercise. Finds the one contiguous change between `prev` and `next` and
 * escapes the inserted part only. `cursor` is where the caret belongs afterwards.
 *
 * Not everything inserted is typed, though. `inputType` is the browser's name for the
 * edit (`InputEvent.inputType`):
 *
 *  - an Undo or Redo (`historyUndo`/`historyRedo`) restores what the field held before,
 *    markup included, and is taken as it is — escaping it turned the `*bold*` an Undo
 *    brought back into literal asterisks;
 *  - a paste or drop (`insertFrom…`) of text `known` to have come out of a form field —
 *    cut, deleted, copied or dragged there — is put back as it was, so moving
 *    `#image(…)` to another line keeps the picture; text from anywhere else is escaped.
 *
 * `removed` is what the edit took out, for the caller's list of `known` text.
 */
export function escapeTyped(
  prev: string,
  next: string,
  opts: { inputType?: string; known?: (text: string) => boolean } = {},
): TypedEdit {
  let p = 0;
  const max = Math.min(prev.length, next.length);
  while (p < max && prev[p] === next[p]) p++;
  let s = 0;
  while (s < max - p && prev[prev.length - 1 - s] === next[next.length - 1 - s]) s++;
  const inserted = next.slice(p, next.length - s);
  const removed = prev.slice(p, prev.length - s);
  const type = opts.inputType ?? '';
  if (/^history(Undo|Redo)$/.test(type) || (inserted && type.startsWith('insertFrom') && opts.known?.(inserted))) {
    return { text: next, cursor: p + inserted.length, removed };
  }
  let escaped = escapePlain(inserted);
  // a typed slash after an existing one would start a comment
  if (escaped.startsWith('/') && next[p - 1] === '/') escaped = '\\' + escaped;
  const text = next.slice(0, p) + escaped + next.slice(next.length - s);
  return { text, cursor: p + escaped.length, removed };
}
